import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Loader2, Upload, X, Info } from "lucide-react";
import { normalizeSenegalPhone } from "../lib/phone";

const CATEGORIES = ["Anniversaire", "Mariage", "Solidarité", "Études", "Voyage", "Projet", "Famille", "Sport", "Autre"];

export default function CreateCagnotte() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    title: "",
    description: "",
    goal_amount: "",
    end_date: "",
    visibility: "public",
    category: "",
    image_url: "",
    payout_wave_phone: "",
    payout_orange_phone: "",
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [publicCfg, setPublicCfg] = useState({ cagnotte_fee_pct: 5 });

  useEffect(() => {
    api.get("/platform/public-config").then(({ data }) => setPublicCfg(data)).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload/image", fd);
      set("image_url", data.path);
      setImagePreview(URL.createObjectURL(file));
      toast.success("Image uploadée");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec du téléchargement");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    // Client-side phone validation & normalization for Wave + Orange Money
    const waveNorm = normalizeSenegalPhone(form.payout_wave_phone);
    if (!waveNorm) { toast.error("Numéro Wave invalide. Formats acceptés : 77XXXXXXX, +221XXXXXXXXX ou 00221XXXXXXXXX"); return; }
    const orangeNorm = normalizeSenegalPhone(form.payout_orange_phone);
    if (!orangeNorm) { toast.error("Numéro Orange Money invalide. Formats acceptés : 77XXXXXXX, +221XXXXXXXXX ou 00221XXXXXXXXX"); return; }
    setLoading(true);
    try {
      const payload = {
        ...form,
        goal_amount: form.goal_amount ? parseFloat(form.goal_amount) : null,
        end_date: form.end_date || null,
        category: form.category || null,
        image_url: form.image_url || null,
        payout_wave_phone: waveNorm,
        payout_orange_phone: orangeNorm,
      };
      const { data } = await api.post("/cagnottes", payload);
      toast.success("Cagnotte créée !");
      navigate(`/cagnotte/${data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="create-cagnotte-page">
      <div className="mb-8">
        <p className="text-sm text-muted-foreground mb-1">Nouvelle cagnotte</p>
        <h1 className="font-display text-4xl font-bold tracking-tight">Lancez votre cagnotte</h1>
      </div>

      <form onSubmit={submit} className="card-soft p-8 sm:p-10 space-y-6">
        {/* Image */}
        <div>
          <Label>Image de couverture</Label>
          <div className="mt-2">
            {imagePreview ? (
              <div className="relative aspect-[16/9] rounded-2xl overflow-hidden bg-secondary group">
                <img src={imagePreview} alt="" className="w-full h-full object-cover" />
                <button type="button" onClick={() => { setImagePreview(null); set("image_url", ""); }} className="absolute top-3 right-3 h-8 w-8 rounded-full bg-black/70 text-white flex items-center justify-center hover:bg-black">
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <label className="aspect-[16/9] rounded-2xl border-2 border-dashed flex flex-col items-center justify-center cursor-pointer hover:border-primary transition-colors text-muted-foreground hover:text-foreground" data-testid="upload-image-label">
                {uploading ? (
                  <><Loader2 className="h-8 w-8 mb-2 animate-spin" /><span className="text-sm">Téléchargement...</span></>
                ) : (
                  <><Upload className="h-8 w-8 mb-2" /><span className="text-sm font-medium">Cliquez pour téléverser</span><span className="text-xs">JPG, PNG (max 8MB)</span></>
                )}
                <input type="file" accept="image/*" onChange={onUpload} className="hidden" disabled={uploading} data-testid="upload-image-input" />
              </label>
            )}
          </div>
        </div>

        <div>
          <Label htmlFor="title">Titre *</Label>
          <Input id="title" required maxLength={140} value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Ex: Anniversaire d'Aïssatou" className="mt-1.5 h-11 rounded-xl" data-testid="cagnotte-title" />
        </div>

        <div>
          <Label htmlFor="description">Description *</Label>
          <Textarea id="description" required rows={5} value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Racontez l'histoire de votre cagnotte..." className="mt-1.5 rounded-xl" data-testid="cagnotte-description" />
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="goal">Objectif (FCFA)</Label>
            <Input id="goal" type="number" min="0" value={form.goal_amount} onChange={(e) => set("goal_amount", e.target.value)} placeholder="Optionnel" className="mt-1.5 h-11 rounded-xl" data-testid="cagnotte-goal" />
          </div>
          <div>
            <Label htmlFor="end_date">Date de fin</Label>
            <Input id="end_date" type="date" value={form.end_date} onChange={(e) => set("end_date", e.target.value)} className="mt-1.5 h-11 rounded-xl" data-testid="cagnotte-enddate" />
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Catégorie</Label>
            <Select value={form.category} onValueChange={(v) => set("category", v)}>
              <SelectTrigger className="mt-1.5 h-11 rounded-xl" data-testid="cagnotte-category">
                <SelectValue placeholder="Choisir une catégorie" />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Visibilité</Label>
            <Select value={form.visibility} onValueChange={(v) => set("visibility", v)}>
              <SelectTrigger className="mt-1.5 h-11 rounded-xl" data-testid="cagnotte-visibility">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="public">Publique</SelectItem>
                <SelectItem value="private">Privée (lien uniquement)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="rounded-2xl border border-emerald-300/60 bg-emerald-50/50 dark:bg-emerald-500/10 dark:border-emerald-500/30 p-4 space-y-3">
          <div className="flex items-center gap-2">
            <Info className="h-4 w-4 text-emerald-700 dark:text-emerald-300" />
            <p className="text-sm font-semibold text-emerald-900 dark:text-emerald-200">Comptes pour recevoir les fonds</p>
          </div>
          <p className="text-xs text-emerald-800/90 dark:text-emerald-200/80">Les contributions Wave seront reversées sur votre numéro Wave et les contributions Orange Money sur votre numéro OM. Formats acceptés : <strong>77XXXXXXX</strong>, <strong>+221XXXXXXXXX</strong> ou <strong>00221XXXXXXXXX</strong>.</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="wave_phone">Numéro Wave *</Label>
              <Input
                id="wave_phone"
                required
                value={form.payout_wave_phone}
                onChange={(e) => set("payout_wave_phone", e.target.value)}
                placeholder="+221771787379"
                inputMode="tel"
                className="mt-1.5 h-11 rounded-xl"
                data-testid="cagnotte-wave-phone"
              />
            </div>
            <div>
              <Label htmlFor="om_phone">Numéro Orange Money *</Label>
              <Input
                id="om_phone"
                required
                value={form.payout_orange_phone}
                onChange={(e) => set("payout_orange_phone", e.target.value)}
                placeholder="+221771787379"
                inputMode="tel"
                className="mt-1.5 h-11 rounded-xl"
                data-testid="cagnotte-orange-phone"
              />
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-amber-300/60 bg-amber-50/70 dark:bg-amber-500/10 dark:border-amber-500/30 p-4 flex gap-3" data-testid="payout-notice">
          <Info className="h-5 w-5 shrink-0 text-amber-700 dark:text-amber-300 mt-0.5" />
          <div className="text-sm leading-relaxed">
            <p className="font-semibold text-amber-900 dark:text-amber-200">Frais de reversement</p>
            <p className="text-amber-800 dark:text-amber-200/90 mt-1">
              Au moment du reversement vers votre compte Wave ou Orange Money, Sen Cagnotte conserve <strong>{publicCfg.cagnotte_fee_pct}%</strong> du montant collecté en commission.
              Vous recevrez donc <strong>{(100 - (publicCfg.cagnotte_fee_pct || 0)).toFixed(0)}%</strong> de la cagnotte.
              Les contributeurs s'acquittent en plus de <strong>{publicCfg.transaction_fee_pct ?? 1}%</strong> de frais de transaction (Wave / OM) — vous ne supportez aucun frais de paiement.
            </p>
          </div>
        </div>

        <div className="flex gap-3 pt-4">
          <Button type="button" variant="outline" onClick={() => navigate(-1)} className="rounded-full" data-testid="cancel-create">Annuler</Button>
          <Button type="submit" disabled={loading} className="btn-gradient rounded-full flex-1 h-11" data-testid="submit-create">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Créer la cagnotte"}
          </Button>
        </div>
      </form>
    </div>
  );
}
